import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Clock, Award, Users, ArrowRight, ArrowUpRight, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Star,
      title: 'Quality First',
      description: 'We use premium materials and precision equipment to ensure every product meets our high standards.'
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: '4-day standard production with rush services available for urgent orders. We understand deadlines matter.'
    },
    {
      icon: Award,
      title: 'Best Prices',
      description: 'Competitive pricing without compromising on quality. We offer the best value in San Diego.'
    },
    {
      icon: Users,
      title: 'Personal Service',
      description: 'Our experienced team provides personalized attention to every customer and every order.'
    }
  ];

  const milestones = [
    { year: '1995', title: 'Founded', description: 'RC Awards opens in San Diego' },
    { year: '2000', title: 'Expansion', description: 'Added promotional products line' },
    { year: '2010', title: 'Technology', description: 'Invested in laser engraving equipment' },
    { year: '2020', title: 'Online Store', description: 'Launched rcawards.com e-commerce' },
    { year: '2026', title: 'Today', description: '200+ 5-star Google reviews' }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-28 pb-20 bg-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">About Us</p>
            <h1 className="text-4xl sm:text-5xl text-[#1a365d] mb-6 font-bold leading-tight">
              San Diego's Trusted Awards Shop Since 1995
            </h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              For nearly 30 years, RC Awards has been the go-to source for custom trophies, awards, plaques, and promotional products in San Diego. What started as a small trophy shop has grown into a full-service recognition company, but our commitment to quality and personal service has never changed.
            </p>
            <p className="text-gray-600 mb-8 leading-relaxed">
              We combine old-school craftsmanship with modern technology to deliver exceptional products at competitive prices. Whether you need a single engraved plaque or thousands of promotional items, we treat every order with the same care and attention to detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="bg-[#D4AF37] text-[#1a365d] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center justify-center gap-2"
              >
                Get in Touch
                <ArrowRight size={14} />
              </Link>
              <a
                href="https://www.rcawards.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-gray-200 text-gray-600 px-6 py-3 rounded-full text-sm font-medium hover:border-gray-300 hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                Visit Our Store
                <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="RC Awards Team"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#1a365d] text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="text-2xl font-bold">5.0 Rating</p>
              <p className="text-white/70 text-sm">200+ Google Reviews</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl text-[#1a365d] mb-4 font-bold">Why Choose RC Awards?</h2>
            <p className="text-gray-600 max-w-lg mx-auto">
              We're committed to delivering quality products and exceptional service on every order.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 border border-gray-100 text-center"
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon size={24} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1a365d] mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl text-[#1a365d] mb-4 font-bold">Our Journey</h2>
            <p className="text-gray-600 max-w-lg mx-auto">
              Nearly three decades of serving the San Diego community.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200 hidden md:block"></div>
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex flex-col md:flex-row items-center gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-xl p-6 border border-gray-100 inline-block">
                      <span className="text-[#D4AF37] font-bold text-lg">{milestone.year}</span>
                      <h3 className="text-xl font-semibold text-[#1a365d] mb-1">{milestone.title}</h3>
                      <p className="text-sm text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-[#D4AF37] rounded-full border-4 border-white shadow-md z-10 hidden md:block"></div>
                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="bg-[#1a365d] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <h2 className="text-3xl font-bold mb-6">Visit Our Showroom</h2>
              <p className="text-white/70 mb-8 leading-relaxed">
                Stop by our shop to see our products in person. Our knowledgeable team is ready to help you find the perfect recognition solution for your needs.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Address</p>
                    <p className="text-white/70">2509 El Cajon Blvd, San Diego, CA 92104</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Phone</p>
                    <a href="tel:6192728317" className="text-white/70 hover:text-[#D4AF37] transition-colors">(619) 272-8317</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Hours</p>
                    <p className="text-white/70">Mon-Fri: 9am-6pm | Sat: 10am-1pm</p>
                  </div>
                </div>
              </div>
              <a
                href="https://maps.google.com/maps?q=2509+El+Cajon+Blvd,+San+Diego,+CA+92104"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D4AF37] text-[#1a365d] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center gap-2"
              >
                Get Directions
                <ArrowUpRight size={14} />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden h-[350px] shadow-xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3356.789!2d-117.13735!3d32.7548!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQ1JzE3LjMiTiAxMTfCsDA4JzE0LjUiVw!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="RC Awards Location"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
